import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import { useBatchExecution } from '../hooks/useBatchExecution';
import type { StatusTone } from '@ui-kit';
import type { BatchJobStatus, StepExecutionDto } from '../types/schedulerMonitor';

interface BatchExecutionDetailDrawerProps {
  executionId: number | null;
  onClose: () => void;
}

function batchStatusTone(status: BatchJobStatus | string): StatusTone {
  if (status === 'COMPLETED') return 'success';
  if (status === 'FAILED' || status === 'ABANDONED') return 'danger';
  if (status === 'STARTED' || status === 'STARTING') return 'info';
  return 'neutral';
}

function formatDateTime(dt: string | null | undefined): string {
  if (!dt) return '-';
  // LocalDateTime from BE has no tz — treat as local
  return new Date(dt).toLocaleString('ko-KR');
}

/** 스텝 진행 막대 */
function StepProgressBar({ step }: { step: StepExecutionDto }) {
  const { t } = useTranslation();
  const total = step.readCount || 1;
  const writePct = Math.min(100, Math.round((step.writeCount / total) * 100));
  const skipPct = Math.min(100, Math.round((step.skipCount / total) * 100));

  return (
    <div
      style={{
        padding: 'var(--spacing-3)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        marginBottom: 'var(--spacing-3)',
        background: 'var(--color-surface-muted)',
      }}
    >
      {/* 스텝명 + 상태 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
          {step.stepName}
        </span>
        <StatusPill tone={batchStatusTone(step.status)}>
          {t(`schedulerMonitor.status.${step.status}`, { defaultValue: step.status })}
        </StatusPill>
      </div>

      {/* 진행 막대 */}
      <div
        style={{
          height: 8,
          borderRadius: 'var(--radius-full)',
          background: 'var(--color-surface-border)',
          overflow: 'hidden',
          marginBottom: 'var(--spacing-2)',
        }}
        role="progressbar"
        aria-valuenow={writePct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${step.stepName} write progress`}
      >
        <div
          style={{
            height: '100%',
            width: `${writePct}%`,
            background: 'var(--color-success)',
            borderRadius: 'var(--radius-full)',
            transition: 'width 0.3s ease',
          }}
        />
      </div>

      {/* 카운트 행 */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-3)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
          flexWrap: 'wrap',
        }}
      >
        <span>
          <strong style={{ color: 'var(--color-text-primary)' }}>{t('schedulerMonitor.detail.step.read')}</strong> {step.readCount.toLocaleString()}
        </span>
        <span>
          <strong style={{ color: 'var(--color-success)' }}>{t('schedulerMonitor.detail.step.write')}</strong> {step.writeCount.toLocaleString()}
          {step.readCount > 0 && <span style={{ color: 'var(--color-text-muted)' }}> ({writePct}%)</span>}
        </span>
        <span>
          <strong style={{ color: 'var(--color-warning)' }}>{t('schedulerMonitor.detail.step.skip')}</strong> {step.skipCount.toLocaleString()}
          {step.readCount > 0 && <span style={{ color: 'var(--color-text-muted)' }}> ({skipPct}%)</span>}
        </span>
        <span>
          <strong style={{ color: 'var(--color-danger)' }}>{t('schedulerMonitor.detail.step.rollback')}</strong> {step.rollbackCount.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

/**
 * BatchExecutionDetailDrawer — 배치 실행 상세 + 스텝 진행 패널 (drawer).
 * role=dialog, aria-modal=true, ESC 닫기.
 */
export function BatchExecutionDetailDrawer({
  executionId,
  onClose,
}: BatchExecutionDetailDrawerProps) {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);
  const { data, isLoading, isError, error, refetch } = useBatchExecution(executionId);

  // ESC 키 닫기
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // 열릴 때 닫기 버튼 포커스
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  if (executionId === null) return null;

  return (
    <>
      {/* 오버레이 */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 900,
          background: 'rgba(0,0,0,0.35)',
        }}
      />

      {/* 드로어 패널 */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="exec-drawer-title"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(40%, 520px)',
          minWidth: 320,
          zIndex: 901,
          background: 'var(--color-surface-background)',
          boxShadow: 'var(--shadow-xl)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--spacing-4) var(--spacing-5)',
            borderBottom: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
          }}
        >
          <h2
            id="exec-drawer-title"
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              margin: 0,
            }}
          >
            {t('schedulerMonitor.detail.exec.title')}
            {data ? ` #${data.executionId}` : ''}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t('schedulerMonitor.detail.close')}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 'var(--font-size-xl)',
              color: 'var(--color-text-secondary)',
              lineHeight: 1,
              padding: 'var(--spacing-1)',
            }}
          >
            ✕
          </button>
        </div>

        {/* 본문 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-5)' }}>
          {/* loading */}
          {isLoading && (
            <div aria-busy="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} style={{ marginBottom: 'var(--spacing-4)' }}>
                  <div
                    style={{
                      height: 10,
                      width: '30%',
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--color-surface-elevated)',
                      marginBottom: 'var(--spacing-1)',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  />
                  <div
                    style={{
                      height: 16,
                      width: '55%',
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--color-surface-elevated)',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* error */}
          {isError && (
            <div
              style={{
                padding: 'var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-danger)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <p>{(error as { message?: string })?.message ?? t('schedulerMonitor.error.loadFailed')}</p>
              <button
                type="button"
                onClick={() => void refetch()}
                style={{
                  marginTop: 'var(--spacing-2)',
                  padding: 'var(--spacing-1) var(--spacing-3)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-xs)',
                  cursor: 'pointer',
                }}
              >
                {t('schedulerMonitor.error.retry')}
              </button>
            </div>
          )}

          {/* default */}
          {data && (
            <>
              {/* 요약 정보 */}
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 1fr',
                  gap: 'var(--spacing-3) var(--spacing-4)',
                  fontSize: 'var(--font-size-sm)',
                  marginBottom: 'var(--spacing-5)',
                }}
              >
                <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                  {t('schedulerMonitor.detail.field.jobName')}
                </dt>
                <dd style={{ margin: 0, fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-primary)' }}>
                  {data.jobName}
                </dd>

                <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 600, alignSelf: 'start' }}>
                  {t('schedulerMonitor.detail.field.status')}
                </dt>
                <dd style={{ margin: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                    <StatusPill tone={batchStatusTone(data.status)}>
                      {t(`schedulerMonitor.status.${data.status}`, { defaultValue: data.status })}
                    </StatusPill>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-mono)' }}>
                      {t('schedulerMonitor.detail.field.exitCode')}: {data.exitCode}
                    </span>
                  </div>
                </dd>

                <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                  {t('schedulerMonitor.detail.field.startTime')}
                </dt>
                <dd style={{ margin: 0, color: 'var(--color-text-primary)' }}>
                  {formatDateTime(data.startTime)}
                </dd>

                <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                  {t('schedulerMonitor.detail.field.endTime')}
                </dt>
                <dd style={{ margin: 0, color: 'var(--color-text-primary)' }}>
                  {formatDateTime(data.endTime)}
                </dd>

                <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                  합계
                </dt>
                <dd style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-mono)' }}>
                  read {data.readCount.toLocaleString()} / write {data.writeCount.toLocaleString()} / skip {data.skipCount.toLocaleString()}
                </dd>
              </dl>

              {/* 스텝 진행 */}
              {data.stepExecutions.length > 0 && (
                <>
                  <h3
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 700,
                      color: 'var(--color-text-primary)',
                      marginBottom: 'var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      paddingBottom: 'var(--spacing-2)',
                    }}
                  >
                    {t('schedulerMonitor.detail.section.steps')}
                  </h3>
                  {data.stepExecutions.map((step) => (
                    <StepProgressBar key={step.stepExecutionId} step={step} />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </>
  );
}
